// ============================================================
// EnvironmentProvider.tsx
// Provider de contexto para gerenciar ambiente em toda a app
// Coloque em: FillSign.App/src/contexts/EnvironmentProvider.tsx
// ============================================================

import React, { createContext, useContext, useEffect, useMemo } from 'react';

const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'development';

type EnvironmentType = 'development' | 'staging' | 'production';

interface EnvironmentContextType {
  environment: EnvironmentType;
  isProduction: boolean;
  isDevelopment: boolean;
  isStaging: boolean;
  isTestEnvironment: boolean;
  environmentLabel: string;
  environmentColor: string;
  showWatermark: boolean;
}

const EnvironmentContext = createContext<EnvironmentContextType | null>(null);

const normalizeEnvironment = (env: string): EnvironmentType => {
  const normalized = env.toLowerCase();
  if (normalized === 'dev' || normalized === 'development') return 'development';
  if (normalized === 'uat' || normalized === 'staging') return 'staging';
  return 'production';
};

export const EnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({ 
  children 
}) => {
  const environment = normalizeEnvironment(ENVIRONMENT);
  
  const value = useMemo<EnvironmentContextType>(() => ({
    environment,
    isProduction: environment === 'production',
    isDevelopment: environment === 'development',
    isStaging: environment === 'staging',
    isTestEnvironment: environment !== 'production',
    environmentLabel: environment === 'development' 
      ? 'Desenvolvimento' 
      : environment === 'staging' 
        ? 'Homologação' 
        : 'Produção',
    environmentColor: environment === 'development' 
      ? '#22c55e' // green-500
      : environment === 'staging' 
        ? '#3b82f6' // blue-500
        : '#6b7280', // gray-500
    showWatermark: environment !== 'production',
  }), [environment]);

  // ============================================================
  // Efeitos de ambiente
  // ============================================================
  useEffect(() => {
    // 1. Atualizar título da página com prefixo de ambiente
    const updateTitle = () => {
      const baseTitle = document.title.replace(/^\[(DEV|UAT)\]\s*/, '');
      if (environment === 'development') {
        document.title = `[DEV] ${baseTitle}`;
      } else if (environment === 'staging') {
        document.title = `[UAT] ${baseTitle}`;
      } else {
        document.title = baseTitle;
      }
    };
    
    updateTitle();
    
    // Observer para manter o prefixo mesmo quando o título mudar
    const observer = new MutationObserver(updateTitle);
    observer.observe(document.querySelector('title')!, { childList: true });
    
    return () => observer.disconnect();
  }, [environment]);

  useEffect(() => {
    // 2. Console warning em ambientes de teste
    if (environment !== 'production') {
      console.log(
        `%c⚠️ AMBIENTE DE ${environment.toUpperCase()} ⚠️`,
        'background: ' + (environment === 'development' ? '#22c55e' : '#3b82f6') + 
        '; color: white; padding: 10px 20px; font-size: 16px; font-weight: bold;'
      );
      console.log(
        '%cEste é um ambiente de teste. Dados não são reais.',
        'color: #666; font-size: 12px;'
      );
    }
  }, [environment]);

  useEffect(() => {
    // 3. Adicionar classe no body para estilização global
    document.body.classList.remove('env-development', 'env-staging', 'env-production');
    document.body.classList.add(`env-${environment}`);
    
    // 4. Adicionar meta tag para bloquear indexação em ambientes de teste
    if (environment !== 'production') {
      let metaRobots = document.querySelector('meta[name="robots"]');
      if (!metaRobots) {
        metaRobots = document.createElement('meta');
        metaRobots.setAttribute('name', 'robots');
        document.head.appendChild(metaRobots);
      }
      metaRobots.setAttribute('content', 'noindex, nofollow');
    }
  }, [environment]);

  useEffect(() => {
    // 5. Adicionar borda colorida na página inteira (visual extra)
    if (environment !== 'production') {
      const borderColor = environment === 'development' ? '#22c55e' : '#3b82f6';
      document.body.style.borderTop = `3px solid ${borderColor}`;
    } else {
      document.body.style.borderTop = 'none';
    }
    
    return () => {
      document.body.style.borderTop = 'none';
    };
  }, [environment]);

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

// ============================================================
// Hook para usar o contexto
// ============================================================
export const useEnvironment = (): EnvironmentContextType => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};

// ============================================================
// Componente condicional baseado no ambiente
// ============================================================
interface EnvironmentOnlyProps {
  children: React.ReactNode;
  environments: EnvironmentType[];
}

export const EnvironmentOnly: React.FC<EnvironmentOnlyProps> = ({ 
  children, 
  environments 
}) => {
  const { environment } = useEnvironment();
  
  if (!environments.includes(environment)) {
    return null;
  }
  
  return <>{children}</>;
};

// ============================================================
// Componente que só aparece em produção
// ============================================================
export const ProductionOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <EnvironmentOnly environments={['production']}>{children}</EnvironmentOnly>
);

// ============================================================
// Componente que só aparece em ambientes de teste
// ============================================================
export const TestOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <EnvironmentOnly environments={['development', 'staging']}>{children}</EnvironmentOnly>
);

export default EnvironmentProvider;
